import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import imageUploadService from '../../../services/imageUpload.service';

/**
 * @typedef {Object} ImageItem
 * @property {File} file - The actual file object
 * @property {string} preview - Object URL for preview
 * @property {string} id - Unique identifier
 * @property {string} status - Upload status: 'pending' | 'uploading' | 'completed' | 'error'
 * @property {string} [url] - Backend URL after upload
 * @property {string} [error] - Error message if upload failed
 */

/**
 * Custom hook for managing image uploads
 * Handles File objects and provides upload functionality to backend
 */
const useImageUpload = (initialImages = []) => {
  const [images, setImages] = useState(initialImages);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);

  /**
   * Cleanup preview URLs on unmount
   */
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.preview && img.preview.startsWith('blob:')) {
          imageUploadService.cleanupPreviewUrl(img.preview);
        }
      });
    };
  }, [images]);

  /**
   * Adds an image to the upload queue
   * @param {File} file - Image file to add
   */
  const uploadImage = useCallback(async (file) => {
    if (!file) return;

    // Validate file using service
    const validation = imageUploadService.validateImageFile(file);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    setIsUploading(true);
    try {
      // Create preview URL
      const preview = imageUploadService.createPreviewUrl(file);
      
      // Create image item
      const imageItem = {
        file,
        preview,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        status: 'pending',
        fileName: file.name,
      };
      
      setImages((prevImages) => [...prevImages, imageItem]);
      toast.success('Image added successfully');
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Failed to add image');
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Removes an image from the array by index
   * @param {number} index - Index of image to remove
   */
  const removeImage = useCallback((index) => {
    setImages((prevImages) => {
      const imageToRemove = prevImages[index];
      
      // Cleanup preview URL
      if (imageToRemove?.preview && imageToRemove.preview.startsWith('blob:')) {
        imageUploadService.cleanupPreviewUrl(imageToRemove.preview);
      }
      
      return prevImages.filter((_, i) => i !== index);
    });
    toast.info('Image removed');
  }, []);

  /**
   * Clears all images
   */
  const clearImages = useCallback(() => {
    // Cleanup all preview URLs
    images.forEach(img => {
      if (img.preview && img.preview.startsWith('blob:')) {
        imageUploadService.cleanupPreviewUrl(img.preview);
      }
    });
    setImages([]);
    setUploadProgress([]);
  }, [images]);

  /**
   * Sets images array (useful for initialization with existing URLs)
   * @param {Array<string>} newImages - Array of image URLs
   */
  const setImageArray = useCallback((newImages) => {
    if (!newImages || newImages.length === 0) {
      setImages([]);
      return;
    }
    
    // Convert URL strings to image items
    const imageItems = newImages.map((url, index) => ({
      preview: url,
      id: `existing-${index}`,
      status: 'completed',
      url: url,
      fileName: url.split('/').pop() || 'image',
    }));
    
    setImages(imageItems);
  }, []);

  /**
   * Uploads all pending images to backend
   * @param {number} productId - Product ID to associate images with
   * @returns {Promise<string[]>} Array of uploaded image URLs
   */
  const uploadImagesToBackend = useCallback(async (productId) => {
    const pendingImages = images.filter(img => img.status === 'pending' && img.file);
    
    if (pendingImages.length === 0) {
      return [];
    }

    setIsUploading(true);
    const uploadedUrls = [];

    try {
      const files = pendingImages.map(img => img.file);
      
      const results = await imageUploadService.uploadMultipleImages(
        files,
        productId,
        (index, progress, status, fileName, result) => {
          // Update progress state
          setUploadProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = { progress, status, fileName, result };
            return newProgress;
          });
          
          // Update image status
          if (status === 'completed' && result?.success) {
            setImages(prevImages => {
              const newImages = [...prevImages];
              const imgIndex = newImages.findIndex(img => img.fileName === fileName && img.status === 'pending');
              if (imgIndex !== -1) {
                newImages[imgIndex] = {
                  ...newImages[imgIndex],
                  status: 'completed',
                  url: result.url,
                };
              }
              return newImages;
            });
          } else if (status === 'error') {
            setImages(prevImages => {
              const newImages = [...prevImages];
              const imgIndex = newImages.findIndex(img => img.fileName === fileName && img.status === 'pending');
              if (imgIndex !== -1) {
                newImages[imgIndex] = {
                  ...newImages[imgIndex],
                  status: 'error',
                  error: result?.error || 'Upload failed',
                };
              }
              return newImages;
            });
          }
        }
      );

      // Collect successful uploads
      results.forEach(result => {
        if (result.success && result.url) {
          uploadedUrls.push(result.url);
        }
      });

      const failedCount = results.filter(r => !r.success).length;
      if (failedCount > 0) {
        toast.warning(`${failedCount} image(s) failed to upload`);
      }

      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
      return uploadedUrls;
    } finally {
      setIsUploading(false);
    }
  }, [images]);

  /**
   * Gets upload progress for display
   * @returns {Array} Upload progress array
   */
  const getUploadProgress = useCallback(() => {
    return uploadProgress;
  }, [uploadProgress]);

  /**
   * Gets array of File objects for backend upload
   * @returns {File[]} Array of File objects
   */
  const getImageFiles = useCallback(() => {
    return images
      .filter(img => img.file && img.status === 'pending')
      .map(img => img.file);
  }, [images]);

  return {
    images,
    isUploading,
    uploadImage,
    removeImage,
    clearImages,
    setImageArray,
    uploadImagesToBackend,
    getUploadProgress,
    getImageFiles,
  };
};

export default useImageUpload;
